import { ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { PageContainer, PageTitle } from "../../global/Common";
import { Box, Card, CardContent, Typography, Switch, FormControlLabel, Divider, Button } from "@mui/material";
import { useSettings } from "../../context/SettingsContext";
import { useLanguage } from "../../context/LanguageContext";
import { useBrand } from "../../context/BrandContext";

const Settings = (): ReactElement => {
  const { t } = useLanguage();
  const { isDiamondsEnabled, toggleDiamonds } = useSettings();
  const { theme } = useBrand();
  const navigate = useNavigate();

  const handleDiamondsToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    toggleDiamonds(event.target.checked);
  };

  return (
    <PageContainer>
      <PageTitle>{t('pages.settings.title')}</PageTitle>
      
      <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <i className="bx bx-cog" style={{ fontSize: 24, color: theme.primary }} />
              {t('pages.settings.display')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ py: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isDiamondsEnabled}
                    onChange={handleDiamondsToggle}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {t('pages.settings.enableDiamonds')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('pages.settings.enableDiamondsDesc')}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <i className="bx bx-info-circle" style={{ fontSize: 24, color: theme.primary }} />
              {t('pages.settings.about')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('pages.settings.version')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('pages.settings.copyright')}
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <i className="bx bx-file-blank" style={{ fontSize: 24, color: theme.primary }} />
              {t('pages.settings.legal')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Button
              variant="text"
              fullWidth
              onClick={() => navigate('/cgu')}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <i className="bx bx-chevron-right" style={{ fontSize: 20 }} />
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {t('pages.settings.cgu')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('pages.settings.cguDesc')}
                  </Typography>
                </Box>
              </Box>
            </Button>
          </CardContent>
        </Card>
      </Box>
    </PageContainer>
  );
};

export default Settings;
